import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/common/Navbar';
import RefreshButton from '../components/common/RefreshButton';
import LoadingSpinner from '../components/common/LoadingSpinner';
import FoodCostTrend from '../components/kitchen/FoodCostTrend';
import BCGScatter from '../components/kitchen/BCGScatter';
import AnomaliesPanel from '../components/kitchen/AnomaliesPanel';
import StopListPanel from '../components/kitchen/StopListPanel';
import CategorySettingsModal from '../components/kitchen/CategorySettingsModal';
import {
  getKitchenAnalysis,
  refreshKitchen,
  searchDish
} from '../services/api';
import { formatMoney, formatNumber, formatDate } from '../utils/formatters';
import './KitchenPage.css';

/* Иконки рисуем штрихом вместо эмодзи — см. Navbar.jsx, откуда взят паттерн. */
const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const ChartIcon = () => <Icon><path d="M3 22H21" /><path d="M3 11C3 10.0572 3 9.58579 3.29289 9.29289C3.58579 9 4.05719 9 5 9C5.94281 9 6.41421 9 6.70711 9.29289C7 9.58579 7 10.0572 7 11V17C7 17.9428 7 18.4142 6.70711 18.7071C6.41421 19 5.94281 19 5 19C4.05719 19 3.58579 19 3.29289 18.7071C3 18.4142 3 17.9428 3 17V11Z" /><path d="M10 7C10 6.05719 10 5.58579 10.2929 5.29289C10.5858 5 11.0572 5 12 5C12.9428 5 13.4142 5 13.7071 5.29289C14 5.58579 14 6.05719 14 7V17C14 17.9428 14 18.4142 13.7071 18.7071C13.4142 19 12.9428 19 12 19C11.0572 19 10.5858 19 10.2929 18.7071C10 18.4142 10 17.9428 10 17V7Z" /><path d="M17 4C17 3.05719 17 2.58579 17.2929 2.29289C17.5858 2 18.0572 2 19 2C19.9428 2 20.4142 2 20.7071 2.29289C21 2.58579 21 3.05719 21 4V17C21 17.9428 21 18.4142 20.7071 18.7071C20.4142 19 19.9428 19 19 19C18.0572 19 17.5858 19 17.2929 18.7071C17 18.4142 17 17.9428 17 17V4Z" /></Icon>;
const TrendIcon = () => <Icon><path d="M22 10.5V12C22 16.714 22 19.0711 20.5355 20.5355C19.0711 22 16.714 22 12 22C7.28595 22 4.92893 22 3.46447 20.5355C2 19.0711 2 16.714 2 12C2 7.28595 2 4.92893 3.46447 3.46447C4.92893 2 7.28595 2 12 2H13.5" /><circle cx="19" cy="5" r="3" /><path d="M7 14L9.29289 11.7071C9.68342 11.3166 10.3166 11.3166 10.7071 11.7071L12.2929 13.2929C12.6834 13.6834 13.3166 13.6834 13.7071 13.2929L17 10M14.5 10H17V12.5" /></Icon>;
const TargetIcon = () => <Icon><path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" /><path d="M2 12L5 12" /><path d="M19 12L22 12" /><path d="M12 22L12 19" /><path d="M12 5L12 2" /><path d="M10 12H12H14" /><path d="M12 14L12 12L12 10" /></Icon>;
const WarningIcon = () => <Icon><path d="M5.31171 10.7615C8.23007 5.58716 9.68925 3 12 3C14.3107 3 15.7699 5.58716 18.6883 10.7615L19.0519 11.4063C21.4771 15.7061 22.6897 17.856 21.5937 19.428C20.4978 21 17.7864 21 12.3637 21H11.6363C6.21356 21 3.50217 21 2.40626 19.428C1.31034 17.856 2.52291 15.7061 4.94805 11.4063L5.31171 10.7615Z" /><path d="M12 8V13" /><path d="M12 16H12.0001" /></Icon>;
const SearchIcon = () => <Icon><circle cx="11.5" cy="11.5" r="9.5" /><path d="M18.5 18.5L22 22" /></Icon>;
const ChefHatIcon = () => <Icon><path d="M5 14.584C3.2341 13.8124 2 12.0503 2 10C2 7.23858 4.23858 5 7 5C7.25052 5 7.49673 5.01842 7.73736 5.05399" /><path d="M19 14.584V18C19 19.8856 19 20.8285 18.4142 21.4142C17.8284 22 16.8856 22 15 22H9C7.11438 22 6.17157 22 5.58579 21.4142C5 20.8285 5 19.8856 5 18V14.584" /><path d="M16.2627 5.05399C16.5033 5.01842 16.7495 5 17.0001 5C19.7615 5 22.0001 7.23858 22.0001 10C22.0001 12.0503 20.766 13.8124 19.0001 14.584" /><path d="M16.5 7V6.5C16.5 5.99416 16.4165 5.50782 16.2626 5.05399C15.6604 3.27806 13.9794 2 12 2C10.0206 2 8.33962 3.27806 7.73736 5.05399C7.58346 5.50782 7.5 5.99416 7.5 6.5V7" /><path d="M5 18H19" /></Icon>;
const WineglassIcon = () => <Icon><path d="M12 15.2856V20.9999M12 20.9999H15.75M12 20.9999H8.25M5 4.89474C5 3.8483 5.8483 3 6.89474 3H17.1053C18.1517 3 19 3.8483 19 4.89474V8C19 11.866 15.866 15 12 15C8.13401 15 5 11.866 5 8V4.89474Z" /><path d="M5.5 9.0001C5.5 9.0001 7.58115 8.08736 9 8.0001C11.4652 7.84847 12.5348 10.1517 15 10.0001C16.4188 9.91283 18.5 9.0001 18.5 9.0001" /></Icon>;
const BoxIcon = () => <Icon><path d="M15.5777 3.38197L17.5777 4.43152C19.7294 5.56066 20.8052 6.12523 21.4026 7.13974C22 8.15425 22 9.41667 22 11.9415V12.0585C22 14.5833 22 15.8458 21.4026 16.8603C20.8052 17.8748 19.7294 18.4393 17.5777 19.5685L15.5777 20.618C13.8221 21.5393 12.9443 22 12 22C11.0557 22 10.1779 21.5393 8.42229 20.618L6.42229 19.5685C4.27063 18.4393 3.19479 17.8748 2.5974 16.8603C2 15.8458 2 14.5833 2 12.0585V11.9415C2 9.41667 2 8.15425 2.5974 7.13974C3.19479 6.12523 4.27063 5.56066 6.42229 4.43152L8.42229 3.38197C10.1779 2.46066 11.0557 2 12 2C12.9443 2 13.8221 2.46066 15.5777 3.38197Z" /><path d="M21 7.5L17 9.5M12 12L3 7.5M12 12V21.5M12 12C12 12 14.7426 10.6287 16.5 9.75C16.6953 9.65237 17 9.5 17 9.5M17 9.5V13M17 9.5L7.5 4.5" /></Icon>;
const BellIcon = () => <Icon><path d="M18.7491 9.70957V9.00496C18.7491 5.13623 15.7274 2 12 2C8.27256 2 5.25087 5.13623 5.25087 9.00496V9.70957C5.25087 10.5552 5.00972 11.3818 4.5578 12.0854L3.45036 13.8095C2.43882 15.3843 3.21105 17.5249 4.97036 18.0229C9.57274 19.3257 14.4273 19.3257 19.0296 18.0229C20.789 17.5249 21.5612 15.3843 20.5496 13.8095L19.4422 12.0854C18.9903 11.3818 18.7491 10.5552 18.7491 9.70957Z" /><path d="M7.5 19C8.15503 20.7478 9.92246 22 12 22C14.0775 22 15.845 20.7478 16.5 19" /></Icon>;
const StarIcon = () => <Icon><path d="M9.15316 5.40838C10.4198 3.13613 11.0531 2 12 2C12.9469 2 13.5802 3.13612 14.8468 5.40837L15.1745 5.99623C15.5345 6.64193 15.7144 6.96479 15.9951 7.17781C16.2757 7.39083 16.6251 7.4699 17.3241 7.62805L17.9605 7.77203C20.4201 8.32856 21.65 8.60682 21.9426 9.54773C22.2352 10.4886 21.3968 11.4691 19.7199 13.4299L19.2861 13.9372C18.8096 14.4944 18.5713 14.773 18.4641 15.1177C18.357 15.4624 18.393 15.8341 18.465 16.5776L18.5306 17.2544C18.7841 19.8706 18.9109 21.1787 18.1449 21.7602C17.3788 22.3417 16.2273 21.8115 13.9243 20.7512L13.3285 20.4768C12.6741 20.1755 12.3469 20.0248 12 20.0248C11.6531 20.0248 11.3259 20.1755 10.6715 20.4768L10.0757 20.7512C7.77268 21.8115 6.62118 22.3417 5.85515 21.7602C5.08912 21.1787 5.21588 19.8706 5.4694 17.2544L5.53498 16.5776C5.60703 15.8341 5.64305 15.4624 5.53586 15.1177C5.42868 14.773 5.19043 14.4944 4.71392 13.9372L4.2801 13.4299C2.60325 11.4691 1.76482 10.4886 2.05742 9.54773C2.35002 8.60682 3.57986 8.32856 6.03954 7.77203L6.67589 7.62805C7.37485 7.4699 7.72433 7.39083 8.00494 7.17781C8.28555 6.96479 8.46553 6.64194 8.82547 5.99623L9.15316 5.40838Z" /></Icon>;
const MoneyBagIcon = () => <Icon><path d="M2 14C2 10.2288 2 8.34315 3.17157 7.17157C4.34315 6 6.22876 6 10 6H14C17.7712 6 19.6569 6 20.8284 7.17157C22 8.34315 22 10.2288 22 14C22 17.7712 22 19.6569 20.8284 20.8284C19.6569 22 17.7712 22 14 22H10C6.22876 22 4.34315 22 3.17157 20.8284C2 19.6569 2 17.7712 2 14Z" /><path d="M16 6C16 4.11438 16 3.17157 15.4142 2.58579C14.8284 2 13.8856 2 12 2C10.1144 2 9.17157 2 8.58579 2.58579C8 3.17157 8 4.11438 8 6" /><path d="M12 17.3333C13.1046 17.3333 14 16.5871 14 15.6667C14 14.7462 13.1046 14 12 14C10.8954 14 10 13.2538 10 12.3333C10 11.4129 10.8954 10.6667 12 10.6667M12 17.3333C10.8954 17.3333 10 16.5871 10 15.6667M12 17.3333V18M12 10V10.6667M12 10.6667C13.1046 10.6667 14 11.4129 14 12.3333" /></Icon>;
const QuestionIcon = () => <Icon><circle cx="12" cy="12" r="10" /><path d="M10.125 8.875C10.125 7.83947 10.9645 7 12 7C13.0355 7 13.875 7.83947 13.875 8.875C13.875 9.56245 13.505 10.1635 12.9534 10.4899C12.478 10.7711 12 11.1977 12 11.75V13" /><path d="M12 16H12.0001" /></Icon>;
const TrendDownIcon = () => <Icon><path d="M2 12C2 7.28595 2 4.92893 3.46447 3.46447C4.92893 2 7.28595 2 12 2C16.714 2 19.0711 2 20.5355 3.46447C22 4.92893 22 7.28595 22 12C22 16.714 22 19.0711 20.5355 20.5355C19.0711 22 16.714 22 12 22C7.28595 22 4.92893 22 3.46447 20.5355C2 19.0711 2 16.714 2 12Z" /><path d="M7 10L9.29289 12.2929C9.68342 12.6834 10.3166 12.6834 10.7071 12.2929L12.2929 10.7071C12.6834 10.3166 13.3166 10.3166 13.7071 10.7071L17 14M14.5 14H17V11.5" /></Icon>;
const WalletMoneyIcon = () => <Icon><path d="M6 10H10" /><path d="M20.8333 11H18.2308C16.4465 11 15 12.3431 15 14C15 15.6569 16.4465 17 18.2308 17H20.8333C20.9167 17 20.9583 17 20.9935 16.9979C21.5328 16.965 21.9623 16.5662 21.9977 16.0654C22 16.0327 22 15.994 22 15.9167V12.0833C22 12.006 22 11.9673 21.9977 11.9346C21.9623 11.4338 21.5328 11.035 20.9935 11.0021C20.9583 11 20.9167 11 20.8333 11Z" /><path d="M20.965 11C20.8873 9.1277 20.6366 7.97975 19.8284 7.17157C18.6569 6 16.7712 6 13 6H10C6.22876 6 4.34315 6 3.17157 7.17157C2 8.34315 2 10.2288 2 14C2 17.7712 2 19.6569 3.17157 20.8284C4.34315 22 6.22876 22 10 22H13C16.7712 22 18.6569 22 19.8284 20.8284C20.6366 20.0203 20.8873 18.8723 20.965 17" /><path d="M6 6L9.73549 3.52313C10.7874 2.82562 12.2126 2.82562 13.2645 3.52313L17 6" /><path d="M17.9912 14H18.0002" /></Icon>;
const ClipboardIcon = () => <Icon><path d="M16 4.00195C18.175 4.01406 19.3529 4.11051 20.1213 4.87889C21 5.75757 21 7.17179 21 10.0002V16.0002C21 18.8286 21 20.2429 20.1213 21.1215C19.2426 22.0002 17.8284 22.0002 15 22.0002H9C6.17157 22.0002 4.75736 22.0002 3.87868 21.1215C3 20.2429 3 18.8286 3 16.0002V10.0002C3 7.17179 3 5.75757 3.87868 4.87889C4.64706 4.11051 5.82497 4.01406 8 4.00195" /><path d="M10.5 14L17 14" /><path d="M7 14H7.5" /><path d="M7 10.5H7.5" /><path d="M7 17.5H7.5" /><path d="M10.5 10.5H17" /><path d="M10.5 17.5H17" /><path d="M8 3.5C8 2.67157 8.67157 2 9.5 2H14.5C15.3284 2 16 2.67157 16 3.5V4.5C16 5.32843 15.3284 6 14.5 6H9.5C8.67157 6 8 5.32843 8 4.5V3.5Z" /></Icon>;
const ClockIcon = () => <Icon><circle cx="12" cy="12" r="10" /><path d="M12 8V12L14.5 14.5" /></Icon>;
const SettingsIcon = () => <Icon><circle cx="12" cy="12" r="3" /><path d="M13.7654 2.15224C13.3978 2 12.9319 2 12 2C11.0681 2 10.6022 2 10.2346 2.15224C9.74457 2.35523 9.35522 2.74458 9.15223 3.23463C9.05957 3.45834 9.0233 3.7185 9.00911 4.09799C8.98826 4.65568 8.70226 5.17189 8.21894 5.45093C7.73564 5.72996 7.14559 5.71954 6.65219 5.45876C6.31645 5.2813 6.07301 5.18262 5.83294 5.15102C5.30704 5.08178 4.77518 5.22429 4.35436 5.5472C4.03874 5.78938 3.80577 6.1929 3.33983 6.99993C2.87389 7.80697 2.64092 8.21048 2.58899 8.60491C2.51976 9.1308 2.66227 9.66266 2.98518 10.0835C3.13256 10.2756 3.3397 10.437 3.66119 10.639C4.1338 10.936 4.43789 11.4419 4.43786 12C4.43783 12.5581 4.13375 13.0639 3.66118 13.3608C3.33965 13.5629 3.13248 13.7244 2.98508 13.9165C2.66217 14.3373 2.51966 14.8691 2.5889 15.395C2.64082 15.7894 2.87379 16.193 3.33973 17C3.80568 17.807 4.03865 18.2106 4.35426 18.4527C4.77508 18.7756 5.30694 18.9181 5.83284 18.8489C6.07289 18.8173 6.31632 18.7186 6.65204 18.5412C7.14547 18.2804 7.73556 18.27 8.2189 18.549C8.70224 18.8281 8.98826 19.3443 9.00911 19.9021C9.02331 20.2815 9.05957 20.5417 9.15223 20.7654C9.35522 21.2554 9.74457 21.6448 10.2346 21.8478C10.6022 22 11.0681 22 12 22C12.9319 22 13.3978 22 13.7654 21.8478C14.2554 21.6448 14.6448 21.2554 14.8477 20.7654C14.9404 20.5417 14.9767 20.2815 14.9909 19.902C15.0117 19.3443 15.2977 18.8281 15.781 18.549C16.2643 18.2699 16.8544 18.2804 17.3479 18.5412C17.6836 18.7186 17.927 18.8172 18.167 18.8488C18.6929 18.9181 19.2248 18.7756 19.6456 18.4527C19.9612 18.2105 20.1942 17.807 20.6601 16.9999C21.1261 16.1929 21.3591 15.7894 21.411 15.395C21.4802 14.8691 21.3377 14.3372 21.0148 13.9164C20.8674 13.7243 20.6602 13.5628 20.3387 13.3608C19.8662 13.0639 19.5621 12.558 19.5621 11.9999C19.5621 11.4418 19.8662 10.9361 20.3387 10.6392C20.6603 10.4371 20.8675 10.2757 21.0149 10.0835C21.3378 9.66273 21.4803 9.13087 21.4111 8.60497C21.3592 8.21055 21.1262 7.80703 20.6602 7C20.1943 6.19297 19.9613 5.78945 19.6457 5.54727C19.2249 5.22436 18.693 5.08185 18.1671 5.15109C17.9271 5.18269 17.6837 5.28136 17.3479 5.4588C16.8545 5.71959 16.2644 5.73002 15.7811 5.45096C15.2977 5.17191 15.0117 4.65566 14.9909 4.09794C14.9767 3.71848 14.9404 3.45833 14.8477 3.23463C14.6448 2.74458 14.2554 2.35523 13.7654 2.15224Z" /></Icon>;
const CloseIcon = () => <Icon><path d="M19.0068 5L5.00684 19" /><path d="M19 19L5 5" /></Icon>;
const StopIcon = () => <Icon><circle cx="12" cy="12" r="10" /><path d="M5 19L19 5" /></Icon>;

const PERIODS = [
  { id: 'week', name: 'Неделя' },
  { id: 'month', name: 'Месяц' },
  { id: 'quarter', name: 'Квартал' }
];

const TABS = [
  { id: 'overview', icon: ChartIcon, label: 'Обзор' },
  { id: 'trend', icon: TrendIcon, label: 'Динамика' },
  { id: 'bcg', icon: TargetIcon, label: 'Матрица BCG' },
  { id: 'anomalies', icon: WarningIcon, label: 'Аномалии' },
  { id: 'stoplist', icon: StopIcon, label: 'Стоп-лист' },
  { id: 'search', icon: SearchIcon, label: 'Поиск блюда' }
];

const DISH_TYPE_LABELS = {
  kitchen: { icon: ChefHatIcon, label: 'Кухня' },
  bar: { icon: WineglassIcon, label: 'Бар' },
  goods: { icon: BoxIcon, label: 'Товары' },
  service: { icon: BellIcon, label: 'Услуги' }
};
const DISH_TYPE_OTHER = { icon: BoxIcon, label: 'Другое' };

const BCG_CARDS = [
  { id: 'stars', icon: StarIcon, title: 'Звёзды', empty: 'Нет звёзд', className: 'stars' },
  { id: 'cows', icon: MoneyBagIcon, title: 'Коровы', empty: 'Нет коров', className: 'cows' },
  { id: 'children', icon: QuestionIcon, title: 'Дети', empty: 'Нет детей', className: 'children' },
  { id: 'dogs', icon: TrendDownIcon, title: 'Собаки', empty: 'Нет собак', className: 'dogs' }
];

const KitchenPage = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('month');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [showCategories, setShowCategories] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const canEditCategories = user?.role === 'admin' || user?.role === 'manager';

  const loadData = useCallback(async (params) => {
    setLoading(true);
    try {
      const data = await getKitchenAnalysis(params);
      setAnalysis(data);
      if (data?.period?.from && data?.period?.to) {
        setDateFrom(data.period.from);
        setDateTo(data.period.to);
      }
    } catch (error) {
      console.error('Error loading kitchen data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData({ period: 'month' });
  }, [loadData]);

  const selectPeriod = (id) => {
    setPeriod(id);
    loadData({ period: id });
  };

  const applyCustomPeriod = () => {
    if (!dateFrom || !dateTo) {
      alert('Выберите обе даты');
      return;
    }
    setPeriod('custom');
    loadData({ from: dateFrom, to: dateTo });
  };

  const currentParams = () =>
    (period === 'custom' ? { from: dateFrom, to: dateTo } : { period });

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshKitchen();
      await loadData(currentParams());
    } finally {
      setRefreshing(false);
    }
  };

  const handleSearch = async () => {
    if (searchQuery.trim().length < 2) {
      alert('Введите минимум 2 символа');
      return;
    }
    setSearching(true);
    setSearchResults(null);
    try {
      setSearchResults(await searchDish(searchQuery.trim()));
    } catch (error) {
      console.error('Search error:', error);
      alert('Ошибка поиска');
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const getFoodCostClass = (foodcost) => {
    if (foodcost > 35) return 'high';
    if (foodcost > 25) return 'warning';
    return 'normal';
  };

  const getMarginClass = (marginPercent) => {
    if (marginPercent > 30) return 'positive';
    if (marginPercent > 15) return 'warning';
    return 'negative';
  };

  const getDishTypeClass = (type) => `type-${type || 'other'}`;

  /* Для фудкоста рост — это плохо, поэтому знак и цвет здесь развязаны:
     «+2.1 п.п.» должно быть красным, а не зелёным, как у выручки. */
  const renderDelta = (value, { unit = 'п.п.', lowerIsBetter = true } = {}) => {
    if (value === undefined || value === null || !analysis?.compare?.has_data) return null;
    const rounded = Math.round(value * 10) / 10;
    if (rounded === 0) return <span className="kt-delta neutral">без изменений</span>;
    const positive = rounded > 0;
    const good = lowerIsBetter ? !positive : positive;
    return (
      <span className={`kt-delta ${good ? 'good' : 'bad'}`}>
        {positive ? '▲' : '▼'} {Math.abs(rounded)} {unit}
      </span>
    );
  };

  if (!isAuthenticated) return null;

  const foodCost = analysis?.foodcost;
  const menuAnalysis = analysis?.menu;
  const structure = foodCost?.revenue_structure;

  const structureRows = [
    { id: 'kitchen', icon: ChefHatIcon, label: 'Кухня', data: structure?.kitchen },
    { id: 'bar', icon: WineglassIcon, label: 'Бар', data: structure?.bar },
    { id: 'goods', icon: BoxIcon, label: 'Товары', data: structure?.goods },
    { id: 'services', icon: BellIcon, label: 'Услуги', data: structure?.services }
  ].filter((row) => (row.data?.revenue || 0) > 0);

  const renderGroupList = (groups, emptyText) => (
    <ul className="dish-list">
      {(groups || []).map((group, index) => (
        <li key={index} className="dish-item">
          <div className="dish-info">
            <div className="dish-name">{group.name}</div>
            <div className="dish-group">{formatMoney(group.revenue)}</div>
          </div>
          <div className="dish-stats">
            <div className={`dish-sum ${getFoodCostClass(group.foodcost)}`}>{group.foodcost}%</div>
          </div>
        </li>
      ))}
      {!groups?.length && <li className="dish-item kt-dim">{emptyText}</li>}
    </ul>
  );

  return (
    <div className="kitchen-container">
      <Navbar user={user} isAdmin={user?.role === 'admin'} onLogout={logout} />

      <div className="kitchen-header">
        <div className="date-badge">
          <span><ClockIcon /></span>
          <span>
            {analysis?.period?.from
              ? `${formatDate(analysis.period.from)} — ${formatDate(analysis.period.to)}`
              : 'Период'}
          </span>
        </div>
        {canEditCategories && (
          <button className="kt-settings-btn" onClick={() => setShowCategories(true)}>
            <SettingsIcon /> Категории
          </button>
        )}
        <RefreshButton onRefresh={handleRefresh} loading={refreshing} />
      </div>

      <div className="kt-period-bar">
        {PERIODS.map((item) => (
          <button
            key={item.id}
            className={`kt-period-btn ${period === item.id ? 'active' : ''}`}
            onClick={() => selectPeriod(item.id)}
          >
            {item.name}
          </button>
        ))}
        <div className="kt-period-custom">
          <input type="date" aria-label="Дата начала" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <span>—</span>
          <input type="date" aria-label="Дата окончания" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          <button
            className={`kt-period-btn ${period === 'custom' ? 'active' : ''}`}
            onClick={applyCustomPeriod}
          >
            Применить
          </button>
        </div>
      </div>

      <div className="ui-tabs kt-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`ui-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon /> {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {analysis?.source_unavailable && (
            <div className={`kt-notice ${analysis.source_loading ? 'warn' : 'kt-notice-error'}`}>
              <span>
                {analysis.source_loading
                  ? 'Данные ещё загружаются из iiko — цифры ниже появятся после выгрузки.'
                  : `iiko не ответил — цифры ниже это не нулевые продажи, а отсутствие
                     данных. Попробуйте обновить через минуту.`}
              </span>
              <button className="ui-btn-ghost" onClick={handleRefresh} disabled={refreshing}>
                Обновить
              </button>
            </div>
          )}

          {foodCost?.unassigned_groups > 0 && canEditCategories && (
            <div className="kt-notice">
              <span>
                {formatNumber(foodCost.unassigned_groups)} групп(ы) блюд не удалось отнести к кухне
                или бару — они учтены как «Товары» и не входят в фудкост.
              </span>
              <button className="ui-btn-ghost" onClick={() => setShowCategories(true)}>
                Разложить по категориям
              </button>
            </div>
          )}

          {activeTab === 'overview' && (
            <>
              <div className="kitchen-stats">
                <div className="stat-card">
                  <div className="stat-header">
                    <div className="stat-icon purple"><WalletMoneyIcon /></div>
                    <div className="stat-title">Общий фудкост</div>
                  </div>
                  <div className="stat-value">{foodCost?.total?.foodcost || 0}%</div>
                  <div className="stat-label">
                    кухня + бар {renderDelta(analysis?.compare?.foodcost_delta)}
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-header">
                    <div className="stat-icon green"><ChefHatIcon /></div>
                    <div className="stat-title">Фудкост кухни</div>
                  </div>
                  <div className="stat-value">{foodCost?.kitchen?.foodcost || 0}%</div>
                  <div className="stat-label">
                    {formatMoney(foodCost?.kitchen?.revenue || 0)}{' '}
                    {renderDelta(analysis?.compare?.kitchen_foodcost_delta)}
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-header">
                    <div className="stat-icon orange"><WineglassIcon /></div>
                    <div className="stat-title">Фудкост бара</div>
                  </div>
                  <div className="stat-value">{foodCost?.bar?.foodcost || 0}%</div>
                  <div className="stat-label">
                    {formatMoney(foodCost?.bar?.revenue || 0)}{' '}
                    {renderDelta(analysis?.compare?.bar_foodcost_delta)}
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-header">
                    <div className="stat-icon blue"><ClipboardIcon /></div>
                    <div className="stat-title">Выручка кухни и бара</div>
                  </div>
                  <div className="stat-value kt-value-sm">
                    {formatMoney(foodCost?.total?.revenue || 0)}
                  </div>
                  <div className="stat-label">
                    {formatNumber(foodCost?.total?.dishes_count || 0)} позиций{' '}
                    {renderDelta(analysis?.compare?.revenue_delta_percent, {
                      unit: '%',
                      lowerIsBetter: false
                    })}
                  </div>
                </div>
              </div>

              {foodCost?.anomalies_count > 0 && (
                <div className="kt-notice warn">
                  <span>
                    {formatNumber(foodCost.anomalies_count)} позиций с некорректной себестоимостью
                    завышают фудкост. Без них он составил бы {foodCost.foodcost_clean?.total || 0}%
                    (кухня {foodCost.foodcost_clean?.kitchen || 0}%).
                  </span>
                  <button className="ui-btn-ghost" onClick={() => setActiveTab('anomalies')}>
                    Посмотреть
                  </button>
                </div>
              )}

              {analysis?.stop_list?.available && analysis.stop_list.count > 0 && (
                <div className="kt-notice warn">
                  <span>
                    {formatNumber(analysis.stop_list.count)} позиций в стопе — до{' '}
                    {formatMoney(analysis.stop_list.total_lost_estimate)} упущенной выручки за период.
                  </span>
                  <button className="ui-btn-ghost" onClick={() => setActiveTab('stoplist')}>
                    Посмотреть
                  </button>
                </div>
              )}

              <div className="kt-panel">
                <h2>Структура выручки</h2>
                <p className="kt-panel-hint">
                  Фудкост считается только по кухне и бару — у товаров и услуг нет
                  себестоимости продукта, и в общем проценте они его исказили бы.
                </p>
                <div className="kt-structure">
                  {structureRows.map((row) => (
                    <div className="kt-structure-row" key={row.id}>
                      <div className="kt-structure-head">
                        <span><row.icon /> {row.label}</span>
                        <span className="kt-structure-value">
                          {formatMoney(row.data.revenue)} · {row.data.share}%
                        </span>
                      </div>
                      <div className={`kt-bar kt-bar-${row.id}`}>
                        <span style={{ width: `${Math.min(row.data.share, 100)}%` }} />
                      </div>
                    </div>
                  ))}
                  {!structureRows.length && <div className="kt-empty">Нет продаж за период</div>}
                </div>
                <div className="kt-structure-total">
                  Всего за период: <strong>{formatMoney(structure?.total || 0)}</strong>
                </div>
              </div>

              <div className="kitchen-two-columns">
                <div className="panel">
                  <h2><span className="badge badge-kitchen"><ChefHatIcon /></span> Фудкост кухни по категориям</h2>
                  {renderGroupList(foodCost?.kitchen_groups, 'Нет данных')}
                </div>
                <div className="panel">
                  <h2><span className="badge badge-bar"><WineglassIcon /></span> Фудкост бара по категориям</h2>
                  {renderGroupList(foodCost?.bar_groups, 'Нет данных')}
                </div>
              </div>

              {foodCost?.goods_groups?.length > 0 && (
                <div className="kitchen-two-columns">
                  <div className="panel">
                    <h2><span className="badge badge-goods"><BoxIcon /></span> Товары</h2>
                    {renderGroupList(foodCost.goods_groups, 'Нет данных')}
                  </div>
                  <div className="panel">
                    <h2><span className="badge badge-service"><BellIcon /></span> Услуги</h2>
                    <ul className="dish-list">
                      {(foodCost.service_groups || []).map((group, index) => (
                        <li key={index} className="dish-item">
                          <div className="dish-info">
                            <div className="dish-name">{group.name}</div>
                            <div className="dish-group">{group.dishes} позиций</div>
                          </div>
                          <div className="dish-stats">
                            <div className="dish-sum normal">{formatMoney(group.revenue)}</div>
                          </div>
                        </li>
                      ))}
                      {!foodCost.service_groups?.length && (
                        <li className="dish-item kt-dim">Нет данных</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === 'trend' && (
            <div className="kt-panel">
              <h2>Фудкост по дням</h2>
              <p className="kt-panel-hint">
                {analysis?.compare?.has_data
                  ? `Предыдущий период (${formatDate(analysis.compare.previous_period.from)} — ${formatDate(analysis.compare.previous_period.to)}): фудкост ${analysis.compare.previous.foodcost}%, выручка ${formatMoney(analysis.compare.previous.revenue)}.`
                  : 'Данных за предыдущий период нет — сравнивать не с чем.'}
              </p>
              <FoodCostTrend trend={analysis?.trend} />
            </div>
          )}

          {activeTab === 'bcg' && (
            <>
              <div className="kt-panel">
                <h2>Матрица BCG</h2>
                <p className="kt-panel-hint">
                  Средняя выручка блюда {formatMoney(menuAnalysis?.avg_revenue || 0)},
                  средняя маржа {menuAnalysis?.avg_margin || 0}% — пунктир на графике
                  и есть границы квадрантов. Позиции с некорректной себестоимостью
                  в матрицу не входят: они смещали бы обе средние.
                </p>
                <BCGScatter menuAnalysis={menuAnalysis} />
              </div>

              <div className="bcg-grid">
                {BCG_CARDS.map((card) => (
                  <div className={`bcg-card ${card.className}`} key={card.id}>
                    <h3><card.icon /> {card.title}</h3>
                    <ul className="bcg-list">
                      {(menuAnalysis?.[card.id] || []).map((item, index) => (
                        <li key={index} className="bcg-item">
                          <div className="bcg-info">
                            <div className="bcg-name">{item.name}</div>
                            <div className="bcg-meta">
                              {formatMoney(item.revenue)} • Маржа {item.margin_percent}%
                            </div>
                          </div>
                          <div className="bcg-amount">{formatNumber(item.amount)} шт.</div>
                        </li>
                      ))}
                      {!menuAnalysis?.[card.id]?.length && (
                        <li className="bcg-item empty">{card.empty}</li>
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === 'anomalies' && <AnomaliesPanel foodCost={foodCost} />}

          {activeTab === 'stoplist' && <StopListPanel stopList={analysis?.stop_list} />}

          {activeTab === 'search' && (
            <div className="search-container">
              <h2><SearchIcon /> Поиск блюда</h2>
              <p className="kt-panel-hint">Поиск идёт по продажам за последние 30 дней.</p>
              <div className="search-input-wrapper">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Введите название блюда..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button className="search-btn" onClick={handleSearch} disabled={searching}>
                  {searching ? 'Поиск...' : <><SearchIcon /> Найти</>}
                </button>
              </div>

              {searchResults && (
                <div className="search-results">
                  <div className="search-results-header">
                    <h3>Результаты поиска</h3>
                    <button onClick={clearSearch} className="clear-search-btn"><CloseIcon /> Очистить</button>
                  </div>
                  <div className="search-results-list">
                    {searchResults.results?.length > 0 ? (
                      searchResults.results.map((dish, index) => (
                        <div key={index} className="search-result-item">
                          <div className="result-header">
                            <div className="result-name">{dish.name}</div>
                            <span className={`result-type ${getDishTypeClass(dish.type)}`}>
                              {(() => {
                                const t = DISH_TYPE_LABELS[dish.type] || DISH_TYPE_OTHER;
                                return <><t.icon /> {t.label}</>;
                              })()}
                            </span>
                          </div>
                          <div className="result-details">
                            <div className="result-group">{dish.group}</div>
                            <div className="result-stats">
                              <div className="result-stat">
                                <div className="stat-label-small">Продано</div>
                                <div className="stat-value-small">{formatNumber(dish.amount)} шт.</div>
                              </div>
                              <div className="result-stat">
                                <div className="stat-label-small">Средняя цена</div>
                                <div className="stat-value-small">{formatMoney(dish.avg_price)}</div>
                              </div>
                              <div className="result-stat">
                                <div className="stat-label-small">Выручка</div>
                                <div className="stat-value-small">{formatMoney(dish.revenue)}</div>
                              </div>
                              <div className="result-stat">
                                <div className="stat-label-small">Себестоимость</div>
                                <div className="stat-value-small">{formatMoney(dish.cost)}</div>
                              </div>
                              <div className="result-stat">
                                <div className="stat-label-small">Маржа</div>
                                <div className={`stat-value-small ${getMarginClass(dish.margin_percent)}`}>
                                  {dish.margin_percent}%
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-results">Ничего не найдено</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {showCategories && (
        <CategorySettingsModal
          onClose={() => setShowCategories(false)}
          onSaved={() => {
            setShowCategories(false);
            loadData(currentParams());
          }}
        />
      )}
    </div>
  );
};

export default KitchenPage;
